import React, { useCallback, useMemo, memo } from "react";
import ReactMarkdown from "react-markdown";
import rehypeHighlight from "rehype-highlight";
import remarkGfm from "remark-gfm";
import "../../assets/styles/github-dark.css";

export const contentToString = (content) => {
  if (content === null || content === undefined) return "";
  if (typeof content === "string") return content;

  if (typeof content === "object") {
    if (content.text !== undefined) {
      return typeof content.text === "string" ? content.text : "";
    }
    if (content.message) return content.message;
    if (content.content && typeof content.content === "string") return content.content;

    try {
      return JSON.stringify(content);
    } catch (e) {
      return "";
    }
  }

  return String(content);
};

// Enhanced highlight text function
export const highlightText = (text, searchQuery, messageIndex) => {
  if (!searchQuery || typeof text !== 'string' || !text) return text;

  try {
    const trimmedQuery = searchQuery.trim();
    if (!trimmedQuery) return text;

    const textStr = String(text);
    const lowerText = textStr.toLowerCase();
    const lowerQuery = trimmedQuery.toLowerCase();
    
    if (!lowerText.includes(lowerQuery)) {
      return text;
    }
    
    const parts = [];
    let lastIndex = 0;
    let matchIndex = lowerText.indexOf(lowerQuery);
    let matchCount = 0;
    
    while (matchIndex !== -1) {
      if (matchIndex > lastIndex) {
        parts.push(textStr.substring(lastIndex, matchIndex));
      }
      
      const exactMatch = textStr.substring(matchIndex, matchIndex + trimmedQuery.length);
      
      const highlightId = `search-match-${messageIndex}-${matchCount}`;
      parts.push(
        <mark 
          key={highlightId}
          id={highlightId}
          className="search-highlight"
          data-message-index={messageIndex}
          data-match-index={matchCount}
        >
          {exactMatch}
        </mark>
      );
      
      matchCount++;
      
      lastIndex = matchIndex + trimmedQuery.length;
      matchIndex = lowerText.indexOf(lowerQuery, lastIndex);
    }
    
    if (lastIndex < textStr.length) {
      parts.push(textStr.substring(lastIndex));
    }
    
    return parts;
  } catch (error) {
    return text;
  }
};

// Extract text content from React elements or objects
const extractTextContent = (node) => {
  if (typeof node === "string") {
    return node;
  }

  if (node === null || node === undefined) {
    return "";
  }

  if (React.isValidElement(node)) {
    if (node.props && node.props.children) {
      if (Array.isArray(node.props.children)) {
        return node.props.children.map(extractTextContent).join("");
      } else {
        return extractTextContent(node.props.children);
      }
    }
    return "";
  }
  
  if (typeof node === "object") {
    if ("value" in node) {
      return node.value;
    }
    
    if (node.text) return node.text;
    if (node.content && typeof node.content === "string") return node.content;
    
    if (Array.isArray(node)) {
      return node.map((item) => extractTextContent(item)).join("");
    }
    
    try {
      const jsonStr = JSON.stringify(node);
      return jsonStr !== "{}" && !jsonStr.includes("[object Object]") ? jsonStr : "";
    } catch (e) {
      return "";
    }
  }

  try {
    const str = String(node);
    return str !== "[object Object]" ? str : "";
  } catch (err) {
    return "";
  }
};

// Add line numbers to code blocks
const addLineNumbers = (code) => {
  const lines = code.split('\n');
  
  return (
    <div className="code-with-line-numbers">
      {lines.map((line, i) => (
        <span key={i} className="line">{line || '\u00A0'}</span>
      ))}
    </div>
  );
};

// Markdown Renderer Component
export const MarkdownRenderer = memo(({ content, searchQuery, messageIndex }) => {
  const remarkPluginsList = useMemo(() => [remarkGfm], []);
  const rehypePluginsList = useMemo(() => [rehypeHighlight], []);

  // Custom component renderer factory for highlighting
  const createHighlightRenderer = useCallback((TagName) => {
    return ({ children, ...props }) => {
      const textContent = extractTextContent(children);
      
      // Add target="_blank" for links
      const linkProps = TagName === 'a' ? { target: '_blank', rel: 'noopener noreferrer' } : {};
      
      if (!searchQuery || !textContent) {
        return <TagName {...props} {...linkProps}>{children}</TagName>;
      }
      
      const lowerText = textContent.toLowerCase();
      const lowerQuery = searchQuery.toLowerCase().trim();
      
      if (!lowerQuery || !lowerText.includes(lowerQuery)) {
        return <TagName {...props} {...linkProps}>{children}</TagName>;
      }
      
      const highlighted = highlightText(textContent, searchQuery, messageIndex);
      
      return <TagName {...props} {...linkProps}>{highlighted}</TagName>;
    };
  }, [searchQuery, messageIndex]);

  // Memoize the pre component for code blocks
  const PreComponent = useMemo(() => {
    return ({ node, children, ...props }) => {
      const codeElement = React.Children.toArray(children).find(
        child => React.isValidElement(child) && child.type === 'code'
      );

      if (!codeElement) {
        return <pre {...props}>{children}</pre>;
      }

      let codeContent = "";
      try {
        const childrenArray = React.Children.toArray(codeElement.props.children);
        codeContent = childrenArray.map(extractTextContent).join("");

        if (codeContent.includes("[object Object]") || codeContent.trim() === "") {
          if (codeElement.props.children?.props?.children) {
            if (typeof codeElement.props.children.props.children === "string") {
              codeContent = codeElement.props.children.props.children;
            } else if (Array.isArray(codeElement.props.children.props.children)) {
              codeContent = codeElement.props.children.props.children
                .map(child => typeof child === "string" ? child : "")
                .join("");
            }
          }
          
          if (codeContent.includes("[object Object]") || codeContent.trim() === "") {
            const rawContent = codeElement.props.children;
            try {
              const jsonStr = JSON.stringify(rawContent, null, 2);
              if (jsonStr && jsonStr !== "{}" && jsonStr !== "null") {
                codeContent = jsonStr;
              }
            } catch (e) {
              // JSON stringify failed, continue with what we have
            }
          }
        }
      } catch (e) {
        codeContent = "Error displaying code";
      }
      
      if (searchQuery && codeContent.toLowerCase().includes(searchQuery.toLowerCase().trim())) {
        const lines = codeContent.split('\n');
        const highlightedLines = lines.map((line, i) => {
          const highlightedLine = highlightText(line, searchQuery, messageIndex);
          return <span key={i} className="line">{highlightedLine}</span>;
        });
        
        return (
          <pre {...props}>
            {React.cloneElement(codeElement, {
              ...codeElement.props,
              children: <div className="code-with-line-numbers">{highlightedLines}</div>
            })}
          </pre>
        );
      }
      
      return (
        <pre {...props}>
          {React.cloneElement(codeElement, {
            ...codeElement.props,
            children: addLineNumbers(codeContent)
          })}
        </pre>
      );
    };
  }, [searchQuery, messageIndex]);

  const markdownComponentsWithHighlight = useMemo(() => {
    return {
      pre: PreComponent,
      p: createHighlightRenderer('p'),
      h1: createHighlightRenderer('h1'),
      h2: createHighlightRenderer('h2'),
      h3: createHighlightRenderer('h3'),
      h4: createHighlightRenderer('h4'),
      h5: createHighlightRenderer('h5'),
      h6: createHighlightRenderer('h6'),
      li: createHighlightRenderer('li'),
      a: createHighlightRenderer('a'),
      strong: createHighlightRenderer('strong'),
      em: createHighlightRenderer('em'),
      blockquote: createHighlightRenderer('blockquote'),
      code: createHighlightRenderer('code'),
      span: createHighlightRenderer('span'),
      td: createHighlightRenderer('td'),
      th: createHighlightRenderer('th'),
      div: createHighlightRenderer('div')
    };
  }, [PreComponent, createHighlightRenderer]);
  
  const stringContent = contentToString(content);
  if (!stringContent || stringContent.trim() === '') {
    return null;
  }

  return (
    <div className="markdown-content">
      <ReactMarkdown
        remarkPlugins={remarkPluginsList}
        rehypePlugins={rehypePluginsList}
        components={searchQuery ? markdownComponentsWithHighlight : {pre: PreComponent, a: createHighlightRenderer('a')}}
      > 
        {stringContent}
      </ReactMarkdown>
    </div>
  );
}, (prevProps, nextProps) => {
  return contentToString(prevProps.content) === contentToString(nextProps.content) && 
         prevProps.searchQuery === nextProps.searchQuery && 
         prevProps.messageIndex === nextProps.messageIndex;
});

export const PlainTextRenderer = memo(({ content, searchQuery, messageIndex }) => {
  const stringContent = contentToString(content);
  if (!stringContent || stringContent.trim() === '') return null;
  
  // Format text with line breaks and highlight search terms
  const renderHighlightedText = () => {
    if (!searchQuery || !searchQuery.trim()) {
      return stringContent.split('\n').map((line, index, array) => (
        <React.Fragment key={index}>
          {line}
          {index < array.length - 1 && <br />}
        </React.Fragment>
      ));
    }
    
    const lines = stringContent.split('\n');
    
    return lines.map((line, lineIndex, array) => {
      const highlightedLine = highlightText(line, searchQuery, messageIndex);
      
      return (
        <React.Fragment key={lineIndex}>
          {highlightedLine}
          {lineIndex < array.length - 1 && <br />}
        </React.Fragment>
      );
    });
  };

  return (
    <div className="plain-text-content">
      {renderHighlightedText()}
    </div>
  );
}, (prevProps, nextProps) => {
  return contentToString(prevProps.content) === contentToString(nextProps.content) && 
         prevProps.searchQuery === nextProps.searchQuery && 
         prevProps.messageIndex === nextProps.messageIndex;
});

// Safe validation function for external components
export const isValidContent = (content) => {
  if (content === null || content === undefined) return false;
  if (content === "") return false;
  if (typeof content === "string") return content.trim() !== "";
  
  // Handle arrays (like content arrays from the API)
  if (Array.isArray(content)) {
    return content.some(item => {
      if (item.type === 'text' && item.text) {
        return item.text.trim() !== "";
      }
      if (item.type === 'tool_result') {
        return true; // Tool results are valid
      }
      // Note: tool_call blocks are not valid content to display
      return false;
    });
  }
  
  if (typeof content === "object") {
    if (content.text !== undefined) {
      return content.text && content.text.trim() !== "";
    }
    return Object.values(content).some((val) => {
      if (typeof val === "string") return val.trim() !== "";
      if (typeof val === "object" && val !== null) {
        return Object.values(val).some(
          (subVal) => typeof subVal === "string" && subVal.trim() !== ""
        );
      }
      return false;
    });
  }
  return false;
};

export default {
  MarkdownRenderer,
  PlainTextRenderer,
  isValidContent,
  contentToString,
  highlightText
};